// OWNER: Agent C. Users list + data-source health. Add-member and disable
// actions call the real Worker once Agent A's routes land — mocked for now.
import { useState } from "react";
import { Text } from "@cloudflare/kumo/components/text";
import { Badge } from "@cloudflare/kumo/components/badge";
import { Button } from "@cloudflare/kumo/components/button";
import { Table } from "@cloudflare/kumo/components/table";
import { Empty } from "@cloudflare/kumo/components/empty";
import { Dialog } from "@cloudflare/kumo/components/dialog";
import { Input } from "@cloudflare/kumo/components/input";
import { useKumoToastManager } from "@cloudflare/kumo/components/toast";
import { PlusIcon } from "@phosphor-icons/react";
import { post } from "../../lib/api";
import { useAdminUsers, createUser, type CreateUserBody } from "../../lib/hooks";
import { PageHeader } from "../../components/PageHeader";
import { TableSkeleton } from "../../components/Skeletons";
import { AccountsManager } from "./AccountsManager";
import { LoansManager } from "./LoansManager";

export default function AdminPage() {
  const users = useAdminUsers();
  const toasts = useKumoToastManager();
  const [refreshing, setRefreshing] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  async function refreshPrices() {
    setRefreshing(true);
    try {
      const result = await post<{ refreshed: number; failed: number }>("/prices/refresh");
      toasts.add({ title: `Refreshed ${result.refreshed} price(s)`, variant: result.failed ? "warning" : "success" });
    } catch (e) {
      toasts.add({ title: "Refresh failed", content: e instanceof Error ? e.message : undefined, variant: "error" });
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Admin"
        action={
          <Button variant="secondary" loading={refreshing} onClick={refreshPrices}>
            Refresh prices now
          </Button>
        }
      />

      <div>
        <div className="flex items-center justify-between">
          <Text variant="heading" as="h2">
            Users
          </Text>
          <Button variant="secondary" icon={<PlusIcon />} onClick={() => setAddMemberOpen(true)}>
            Add member
          </Button>
        </div>
        <div className="mt-3">
          {users.status === "loading" && <TableSkeleton rows={3} />}
          {users.status === "error" && <Text variant="error">{users.error.message}</Text>}
          {users.status === "success" && users.data.length === 0 && <Empty size="sm" title="No users yet" />}
          {users.status === "success" && users.data.length > 0 && (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Name</Table.Head>
                  <Table.Head>Email</Table.Head>
                  <Table.Head>Role</Table.Head>
                  <Table.Head>Status</Table.Head>
                  <Table.Head>Email delivery</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {users.data.map((u) => (
                  <Table.Row key={u.id}>
                    <Table.Cell>{u.name ?? "—"}</Table.Cell>
                    <Table.Cell>{u.email}</Table.Cell>
                    <Table.Cell>
                      <Badge variant={u.role === "admin" ? "primary" : "neutral"}>{u.role}</Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant={u.status === "active" ? "success" : "neutral"}>{u.status}</Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {u.emailVerifiedForDelivery == null ? (
                        <Badge variant="neutral">Unknown</Badge>
                      ) : u.emailVerifiedForDelivery ? (
                        <Badge variant="success">Verified</Badge>
                      ) : (
                        <Badge variant="error">Not verified — OTPs will silently fail</Badge>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>
      </div>

      <AddMemberDialog open={addMemberOpen} onClose={() => setAddMemberOpen(false)} onAdded={users.reload} />

      <AccountsManager />

      <LoansManager />
    </div>
  );
}

function AddMemberDialog({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: () => void }) {
  const toasts = useKumoToastManager();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setEmail("");
    setName("");
    setError(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      onClose();
      reset();
    }
  }

  async function handleSubmit() {
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body: CreateUserBody = { email: email.trim(), name: name.trim() || null };
      await createUser(body);
      toasts.add({ title: "Member added", variant: "success" });
      onAdded();
      onClose();
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog className="p-6">
        <Dialog.Title className="text-lg font-semibold">Add member</Dialog.Title>
        <Text variant="secondary" size="sm">
          New members land as a regular member — this app only ever has one admin, set up once by hand.
        </Text>
        <div className="mt-4 space-y-4">
          <Input label="Email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e.g. someone@example.com" />
          <Input label="Name" required={false} value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
        </div>
        {error && (
          <div className="mt-3">
            <Text variant="error" size="sm">
              {error}
            </Text>
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={submitting} onClick={handleSubmit}>
            Add member
          </Button>
        </div>
      </Dialog>
    </Dialog.Root>
  );
}
